import { createRouter, createWebHistory } from 'vue-router';
import { trackNavigation } from '@/router/apaluma-analytics.ts';
import { maintenancePageGuard } from '@/router/maintenance-page-guard.ts';
import { useNmedDocAiUiStore } from '@/stores/nmed/nmed-doc-ai-ui-store.ts';
import { useNmedDocSearchFilterStore } from '@/stores/nmed/nmed-doc-search-filter-store.ts';
import { adminPageGuard } from './admin-page-guard';
import { authGuard } from './auth-guard';

/* ---------------------------------------------------------------------------------------------- */

// Test UI routes
const testRoutes = [
   {
      path: '/test/ui',
      name: 'test-ui',
      component: () => import('@/views/test/ui/test-ui-page.vue'),
      meta: {
         requiresAuth: true,
      },
   },
   {
      path: '/test/design-system',
      name: 'design-system-playground',
      component: () => import('@/views/test/ui/design-system-playground.vue'),
      meta: {
         requiresAuth: true,
      },
   },
];

/* ---------------------------------------------------------------------------------------------- */

// Public portal routes (no authentication required)
const publicRoutes = [
   {
      path: '/public',
      name: 'public-portal',
      component: () => import('@/views/public/public-portal-page.vue'),
      redirect: '/public/nm-regulatory-assessment',
      children: [
         {
            path: 'nm-regulatory-assessment',
            name: 'public-nm-regulatory-assessment',
            component: () => import('@/views/public/nm-regulatory-assessment-page.vue'),
         },
         {
            path: 'pre-permitting',
            name: 'public-pre-permitting',
            component: () => import('@/views/public/pre-permitting-page.vue'),
         },
         {
            path: 'application-submission',
            name: 'public-application-submission',
            component: () => import('@/views/public/application-submission-page.vue'),
         },
         {
            path: 'regulator',
            name: 'public-regulator-dashboard',
            component: () => import('@/views/public/regulator-dashboard-page.vue'),
         },
         {
            path: 'regulator/application/:applicationId',
            name: 'public-application-review',
            component: () => import('@/views/public/application-review-page.vue'),
         },
      ],
   },
];

/* ---------------------------------------------------------------------------------------------- */

const router = createRouter({
   history: createWebHistory(import.meta.env.BASE_URL),
   scrollBehavior(_to, _from, savedPosition) {
      return savedPosition || { top: 0 };
   },
   routes: [
      {
         path: '/',
         redirect: '/search',
      },
      {
         path: '/chats',
         name: 'home',
         component: () => import('@/views/landing-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/chats/all',
         name: 'chats-all',
         component: () => import('@/views/chats-all-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/chats/:sessionId',
         name: 'chat',
         component: () => import('@/views/landing-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/documents',
         name: 'documents',
         component: () => import('@/views/documents-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/map',
         name: 'map',
         component: () => import('@/views/map-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/saved-documents',
         name: 'saved-documents',
         component: () => import('@/views/saved-documents-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/search',
         name: 'search',
         component: () => import('@/views/search-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects',
         name: 'projects',
         component: () => import('@/views/projects-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/facilities',
         name: 'facilities',
         component: () => import('@/views/facilities-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/facilities/:facilityId',
         name: 'facility',
         component: () => import('@/views/facility-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/facilities/:facilityId/building',
         name: 'facility-building',
         component: () => import('@/views/facility-building-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId',
         name: 'project',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId/chats',
         name: 'project-chat',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/projects/:projectId/chats/:threadId',
         name: 'project-chat-thread',
         component: () => import('@/views/project-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/about',
         name: 'about',
         component: () => import('@/views/about-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/settings',
         name: 'settings',
         component: () => import('@/views/settings-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/learning-library',
         name: 'learning-library',
         component: () => import('@/views/learning-library-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/admin',
         name: 'admin',
         component: () => import('@/views/admin-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },
      {
         path: '/under-maintenance',
         name: 'under-maintenance',
         component: () => import('@/views/maintenance-page.vue'),
      },
      {
         path: '/cookie-policy',
         name: 'cookie-policy',
         component: () => import('@/views/cookie-policy-page.vue'),
      },
      {
         path: '/login',
         name: 'login',
         component: () => import('@/views/auth/login-page.vue'),
      },
      {
         path: '/auth/callback',
         name: 'cognito-callback',
         component: () => import('@/views/auth/callbacks/cognito-callback-page.vue'),
      },
      {
         path: '/signup',
         name: 'signup',
         component: () => import('@/views/auth/signup-page.vue'),
      },
      {
         path: '/forgot-password',
         name: 'forgot-password',
         component: () => import('@/views/auth/forgot-password-page.vue'),
      },

      {
         path: '/confirm-signup',
         name: 'confirm-signup',
         component: () => import('@/views/auth/confirm-signup-page.vue'),
      },
      {
         path: '/logout',
         name: 'logout',
         component: () => import('@/views/auth/logout-page.vue'),
         meta: {
            requiresAuth: true,
         },
      },

      ...testRoutes,
      ...publicRoutes,
   ],
});

router.beforeEach(maintenancePageGuard);
router.beforeEach(authGuard);
router.beforeEach(adminPageGuard);
router.afterEach(trackNavigation);

// Reset document UI state when navigating to a different route
router.afterEach((to, from) => {
   // Only reset if actually navigating between different routes
   // (not on initial load or same-route navigation)
   if (from.name && to.path !== from.path) {
      const docAiUiStore = useNmedDocAiUiStore();
      const docSearchFilterStore = useNmedDocSearchFilterStore();
      docAiUiStore.closeDocAiWindow();

      // Save current filter state before clearing (for navigation recovery)
      const currentState = docSearchFilterStore.getSerializableState();
      const hasFilters = currentState.bureaus.length > 0
         || currentState.documentTypes.length > 0
         || Object.keys(currentState.searchFilters || {}).length > 0
         || currentState.globalSearchKeyword
         || currentState.year
         || currentState.startYear
         || currentState.endYear
         || currentState.sortField;

      // Persist filters before clearing so they can be restored when returning
      if (hasFilters) {
         try {
            sessionStorage.setItem('doc-search-filter-state', JSON.stringify(currentState));
         }
         catch {
            // sessionStorage not available
         }
      }

      docSearchFilterStore.clearAllFilters();
   }
});

export default router;
